"use client";

import { useSession } from "@workspace/auth/client";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { getClientHost, isEmbeddedSurface } from "@/lib/host";
import { useHost } from "@/lib/host-context";

export function SessionGuard() {
  const { data: session, isPending } = useSession();
  const hadSession = useRef(false);
  const host = useHost();

  useEffect(() => {
    if (isPending) {
      return;
    }

    if (session) {
      hadSession.current = true;
      return;
    }

    if (hadSession.current) {
      // In ANY embedded host, auth belongs to the surface's own gate — MSAL
      // NAA for the Office add-ins, the top-level sign-in popup for Procore —
      // never the Better Auth SSO browser-redirect flow. Redirecting here
      // sends the WebView/iframe itself toward login.microsoftonline.com:
      // iframes get X-Frame-Options: DENY, and Office WebViews bounce the
      // navigation to the system browser while the state cookie stays in the
      // WebView jar (state_mismatch). HostProvider's gate overlays a sign-in
      // screen when the session is genuinely gone.
      //
      // Check the sessionStorage marker as well as context: the context host
      // comes from the `magnus-host` cookie, which embedded WebViews are
      // known to drop (see office-shell.tsx), while sessionStorage is written
      // in-frame by the bootstrap and survives cookie blocking.
      if (isEmbeddedSurface(host) || isEmbeddedSurface(getClientHost())) {
        console.warn(
          "[Magnus] Session expired in embedded context — skipping SSO redirect"
        );
        return;
      }
      toast.error("Your session ended — please sign in again");
      // `prompt=login` forces a genuine interactive re-authentication. A
      // session that vanishes mid-use was revoked (e.g. the server signs
      // users out when their Microsoft grant needs interactive re-auth —
      // ADR-072) or expired; silent SSO here would let Entra re-issue
      // tokens off the same stale browser session without re-running
      // MFA/Conditional Access, landing the user in a sign-out loop.
      window.location.href =
        "/api/auth/sso?callbackUrl=" +
        encodeURIComponent(window.location.pathname) +
        "&prompt=login";
    }
  }, [host, session, isPending]);

  return null;
}
